import React, { useContext, useEffect, useRef, useState } from "react";
import styles from "../styles/Chatbot.module.css";
import { GoPaperclip } from "react-icons/go";
import { VscSend } from "react-icons/vsc";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Typed from "typed.js";
import ChatList from "../components/ChatList";
import UserContext from "../context/user/UserContext";
import StateContext from "../context/state/StateContext";
import axios from "axios";
import { GoArrowDown } from "react-icons/go";
import { FaRegSquare } from "react-icons/fa6";
import { MdDelete } from "react-icons/md";
import Sidebar from "../components/Sidebar";
import { FaFilePdf, FaFileWord, FaFileExcel, FaFileImage, FaFileAlt } from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";

const Chatbot = () => {
  const [text, setText] = useState("");
  const [filePreview, setFilePreview] = useState(null); // State for single file preview
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [disabled, setDisabled] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [currentReply, setCurrentReply] = useState(false);
  const token = localStorage.getItem("token");
  const [loader, setLoader] = useState(false);
  const contentRef = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [pageLoad, setPageLoad] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const navigate = useNavigate();

  const [params] = useSearchParams();

  const fetchMessagesForChat = async (chatId) => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL + "api/chatbot/getMessages",
        {
          params: { chatId: chatId },
          headers: {
            "Content-Type": "application/json",
            "auth-token": token,
          },
        }
      );
      console.log("response Is", response.data.messages);

      if (response.data.success) {
        setChatMessages(response.data.messages);
      } else {
        setChatMessages([]);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  useEffect(() => {
    const chatId = params.get("chat");
    if (chatId && !selectedChat) {
      setSelectedChat(chatId);
    }
  }, []);

  useEffect(() => {
    if (selectedChat) {
      fetchMessagesForChat(selectedChat);
    }
  }, [selectedChat]);

  const handleChange = (e) => {
    const value = e.target.value;
    setText(value);
    autoResize();
    setDisabled(value.length === 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.shiftKey) {
      e.preventDefault();
      insertAtCursor("\n");
      setTimeout(() => autoResize(), 0);
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData("text");
    const markdownText = formatToMarkdown(pastedText);
    insertAtCursor(markdownText);
    setDisabled(false);
    setTimeout(() => autoResize(), 0);
  };

  const formatToMarkdown = (content) => {
    return content
      .split("\n")
      .map((line) => {
        if (line.trim().startsWith("-") || line.trim().startsWith("*")) {
          return `- ${line.trim().replace(/^[-*]\s*/, "")}`;
        }
        return line;
      })
      .join("\n");
  };

  const insertAtCursor = (insertText) => {
    if (textareaRef.current) {
      const cursorPosition = textareaRef.current.selectionStart;
      const beforeCursor = text.slice(0, cursorPosition);
      const afterCursor = text.slice(cursorPosition);
      const newText = beforeCursor + insertText + afterCursor;
      setText(newText);

      setTimeout(() => {
        textareaRef.current.selectionStart = textareaRef.current.selectionEnd =
          cursorPosition + insertText.length;
      }, 0);
    }
  };

  const autoResize = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0]; // Only take the first file
    if (file) {
      let fileIcon;
      if (file.type.includes("pdf")) {
        fileIcon = <FaFilePdf className="file-icon pdf" />;
      } else if (file.type.includes("word")) {
        fileIcon = <FaFileWord className="file-icon word" />;
      } else if (file.type.includes("excel")) {
        fileIcon = <FaFileExcel className="file-icon excel" />;
      } else if (file.type.includes("image")) {
        fileIcon = <FaFileImage className="file-icon image" />;
      } else {
        fileIcon = <FaFileAlt className="file-icon default" />;
      }

      setFilePreview({
        name: file.name,
        type: file.type,
        icon: fileIcon,
        preview: file.type.includes("image") ? URL.createObjectURL(file) : null,
        file: file,
      });
    }
  };

  const removeFile = () => {
    setFilePreview(null);
    fileInputRef.current.value = ""; // Clear the file input
  };

  const sendChatbotMessage = async (message, fileUrl = null) => {
    try {
      scrollToBottom();
      setLoader(true);

      const response = await fetch(
        process.env.REACT_APP_API_URL + "api/chatbot/send",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "auth-token": token,
          },
          body: JSON.stringify({
            prompt: message,
            fileUrl: fileUrl, // Single file URL
            chat_title: selectedChat || message,
            chatId: selectedChat,
          })
        }
      );

      const json = await response.json();
      console.log(json);
      try {
        setCurrentReply(true);
        if (json.success) {
          setChatMessages((prevMessages) => {
            if (prevMessages.length === 0) return [json.response];
            return [
              ...prevMessages.slice(0, -1),
              { bot_reply: json.response, user_message: message, files: fileUrl ? [fileUrl] : [] },
            ];
          });
          if (!selectedChat) {
            navigate(`/dashboard/chatbot?chat=${json.chatId}`);
          }
        } else {
          setChatMessages((prevMessages) => {
            if (prevMessages.length === 0)
              return {
                user_message: text,
                bot_reply: json.message || "Some error occurred",
              };
            return [
              ...prevMessages.slice(0, -1),
              {
                user_message: text,
                bot_reply: json.message || "Some error occurred",
              },
            ];
          });
        }
      } catch (error) {
        console.log(error);
      }
    } catch (error) {
      console.log(error);
      setChatMessages((prevMessages) => {
        if (prevMessages.length === 0)
          return {
            user_message: text,
            bot_reply: "Some error occurred",
          };
        return [
          ...prevMessages.slice(0, -1),
          {
            user_message: text,
            bot_reply: "Some error occurred",
          },
        ];
      });
    } finally {
      setLoader(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (!disabled && !loader) {
      if (text.trim() || filePreview) {
        let userMessage = { user_message: text };
        setChatMessages([...chatMessages, userMessage]);

        if (filePreview) {
          const formData = new FormData();
          formData.append("file", filePreview.file); // Append single file

          try {
            const uploadResponse = await axios.post(
              `${process.env.REACT_APP_API_URL}api/chatbot/upload`,
              formData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                  "auth-token": token,
                },
              }
            );

            if (uploadResponse.data.success) {
              const fileUrl = uploadResponse.data.fileUrl;

              // Custom Markdown Format for Uploaded File
              const uploadedFileMarkdown = `📎 **Attached File:** [${filePreview.name}](${fileUrl})`;
              setChatMessages((prevMessages) =>
                prevMessages.map((msg) =>
                  msg === userMessage
                    ? {
                      ...msg,
                      user_message: text + `\n\n${uploadedFileMarkdown}`,
                    }
                    : msg
                )
              );

              // Send the final message to the chatbot with the file URL
              sendChatbotMessage(text + `\n\n${uploadedFileMarkdown}`, fileUrl);
            }
          } catch (error) {
            console.error("Error uploading file:", error);
          }
        } else {
          // If no file, send the text message
          sendChatbotMessage(text);
        }

        setText("");
        setFilePreview(null);
        if (textareaRef.current) textareaRef.current.style.height = "auto";
      }
    }
  };

  const scrollToBottom = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const handleScroll = async () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      setShowScrollButton(scrollHeight - (scrollTop + clientHeight) > 50);
      if (scrollTop === 0 && hasMore && !loading) {
        setLoading(true);
        const previousScrollHeight = contentRef.current.scrollHeight;

        try {
          setTimeout(() => {
            if (contentRef.current) {
              contentRef.current.style.scrollBehavior = "auto";
              contentRef.current.scrollTop =
                contentRef.current.scrollHeight - previousScrollHeight;
              setTimeout(() => {
                contentRef.current.style.scrollBehavior = "smooth";
              }, 50);
            }
          }, 100);
        } catch (error) {
          console.error("Error fetching messages:", error);
        } finally {
          setLoading(false);
        }
      }
    }
  };

  const deleteChat = async (chatId) => {
    try {
      setLoading(true);
      const token_1 = localStorage.getItem("token");
      console.log(token_1);

      const response = await axios.post(
        process.env.REACT_APP_API_URL + "api/chatbot/deleteChat",
        {
          thread_id: chatId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            "auth-token": token_1,
          },
        }
      );
      console.log("All Chats Are", response);

      if (response.data.success) {
        console.log("Chat Delete success", response.data.chats);
        window.location.href = "/dashboard/chatbot";
      }
    } catch (error) {
      console.error("Error fetching chats:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    const content = contentRef.current;
    if (content) {
      content.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (content) {
        content.removeEventListener("scroll", handleScroll);
      }
    };
  });

  useEffect(() => {
    if (!pageLoad || text) {
      scrollToBottom();
    }
  }, [pageLoad, text]);

  const loadingSpinner = () => {
    return (
      <div className={styles.dot_spinner}>
        <div className={styles.dot_spinner__dot}></div>
        <div className={styles.dot_spinner__dot}></div>
        <div className={styles.dot_spinner__dot}></div>
        <div className={styles.dot_spinner__dot}></div>
        <div className={styles.dot_spinner__dot}></div>
        <div className={styles.dot_spinner__dot}></div>
        <div className={styles.dot_spinner__dot}></div>
        <div className={styles.dot_spinner__dot}></div>
      </div>
    );
  };

  if (!pageLoad) {
    return (
      <div className={styles.container}>
        <Sidebar setSelectedChat={setSelectedChat} setLoading={setLoading} />
        {chatMessages && chatMessages.length > 0 ? (
          <>
            <div className={styles.content} ref={contentRef}>
              {loading && <div>{loadingSpinner()}</div>}
              {chatMessages.map((message, index) => (
                <div key={index} className={styles.chat_list}>
                  <ChatList
                    scrollToBottom={scrollToBottom}
                    currentReply={currentReply}
                    setCurrentReply={setCurrentReply}
                    index={index}
                    length={chatMessages.length}
                    loader={loader}
                    message={message.user_message}
                    replyed={message.bot_reply}
                    files={message.files}
                  />
                </div>
              ))}
            </div>
          </>
        ) : (
          <h1>What can I help with?</h1>
        )}
        <form onSubmit={handleSubmit} className={styles.inputs}>
          {filePreview && (
            <div className={styles.filePreviews}>
              <div key={0} className={styles.filePreview}>
                <div onClick={removeFile} className={styles.close_icon}>
                  X
                </div>
                {filePreview.preview ? (
                  <div className={styles.icon}>
                    <img src={filePreview.preview} alt="Preview" className="file-preview-image" />
                  </div>
                ) : (
                  <div className={styles.icon}>{filePreview.icon}</div>
                )}
                <div>
                  <p className="!mb-0 line-clamp-1">Name:{filePreview.name}</p>
                  <p className="text-orange-200">type:{filePreview.type}</p>
                </div>
              </div>
            </div>
          )}

          {showScrollButton && (
            <button
              type={"button"}
              onClick={() => {
                scrollToBottom();
              }}
              className={styles.scroll}
            >
              <GoArrowDown />
            </button>
          )}
          <button
            type="button"
            onClick={() => fileInputRef.current.click()}
            className={styles.btn}
          >
            <GoPaperclip />
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              className="d-none"
            />
          </button>

          <textarea
            ref={textareaRef}
            value={text}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            rows={1}
            placeholder="Type something..."
          />
          <button
            type="submit"
            disabled={disabled || loader}
            className={`${styles.btn} ${disabled || loader ? styles.disabled : ""}`}
          >
            {loader ? <FaRegSquare /> : <VscSend />}
          </button>
        </form>
      </div>
    );
  } else {
    return <div className={styles.container}>{loadingSpinner()}</div>;
  }
};

export default Chatbot;